import * as potreeState from '../potreeState';

const getPointCloudUrl = (url: string, retries: number) => {
  // Local projects fetch directly from Azurite
  if (potreeState.isLocalProject && potreeState.storageBaseUrl) {
    const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
    return potreeState.storageBaseUrl + '/' + cleanUrl;
  }

  const FALLBACK_URL = window.location.host === 'app.pointorama.com' ? 'https://productionpointorama.blob.core.windows.net/' : 'https://pointxploreraccstate.blob.core.windows.net/';
  const CDN_URL = window.location.host === 'app.pointorama.com' ? 'https://productionpointorama.azureedge.net/' : 'https://pointxplorerv3.azureedge.net/';
  const prefix = retries > 2 ? FALLBACK_URL : CDN_URL;
  return prefix + url;
};

const MAX_RETRIES = 4;
// From this retry on, requests go to storage directly instead of the CDN (see getPointCloudUrl).
const FALLBACK_RETRY = 3;

class NotFoundError extends Error {}

export interface FetchFileOptions {
  /**
   * The file may legitimately not exist (the segment list of a cloud that was not segmented). A 404
   * from storage is then final: no retries, no backoff, nothing logged, and the result is undefined.
   * A CDN 404 still goes to storage once, right away, since the CDN can miss a file storage has.
   */
  optional?: boolean;
}

export const fetchFileBuffer = async (url: string, options: FetchFileOptions = {}) => {
  let retryCount = 0;
  let buffer: ArrayBuffer | undefined;
  // Set when the next attempt should go out at once (an optional file's CDN miss).
  let skipDelay = false;

  // fetch('soadifj', {priority: 'low'})

  const fetcher = async () => {
    if (retryCount && !skipDelay) await new Promise((resolve) => setTimeout(resolve, retryCount * 1000));
    skipDelay = false;
    buffer = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const parsedUrl = getPointCloudUrl(url, retryCount);
      xhr.open('GET', parsedUrl, true);
      xhr.responseType = 'arraybuffer';
      // Cache-Control header disabled - caused issues in some environments
      xhr.overrideMimeType('text/plain; charset=x-user-defined');

      // Set timeout to prevent hanging requests (30 seconds)
      xhr.timeout = 30000;
      xhr.ontimeout = () => {
        retryCount++;
        if (retryCount <= MAX_RETRIES) fetcher().then(resolve).catch(reject);
        else reject(new Error('Request timeout after retries'));
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if ((xhr.status === 200 || xhr.status === 0) && xhr.response !== null) {
            const buffer = xhr.response;
            resolve(buffer);
          } else if (xhr.status === 404 && options.optional) {
            // Local storage and the storage fallback are authoritative: the file is not there.
            const fromCdn = !(potreeState.isLocalProject && potreeState.storageBaseUrl) && retryCount < FALLBACK_RETRY;
            if (!fromCdn) {
              reject(new NotFoundError(url));
              return;
            }
            retryCount = FALLBACK_RETRY;
            skipDelay = true;
            fetcher().then(resolve).catch(reject);
          } else {
            retryCount++;
            if (retryCount <= MAX_RETRIES) fetcher().then(resolve).catch(reject);
            else reject(new Error(`HTTP error: ${xhr.status}`));
          }
        }
      };
      try {
        xhr.send(null);
      } catch (e) {
        retryCount++;
        if (retryCount <= MAX_RETRIES) fetcher().then(resolve).catch(reject);
        else reject(e);
      }
    });
    return buffer;
  };
  try {
    await fetcher();
  } catch (e) {
    if (e instanceof NotFoundError) return undefined;
    console.log('fetching failed');
    // Return undefined but don't throw - let callers handle missing buffer gracefully
  }
  return buffer;
};
